import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

interface SetThemeOptions {
  persist?: boolean;
}

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme, options?: SetThemeOptions) => void;
}

export const THEME_STORAGE_KEY = 'theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return Theme.LIGHT;

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === Theme.LIGHT || stored === Theme.DARK) {
    return stored;
  }

  return Theme.LIGHT;
}

function applyThemeClass(theme: Theme) {
  const root = document.documentElement;
  if (theme === Theme.DARK) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  const setTheme = useCallback((next: Theme, options?: SetThemeOptions) => {
    const persist = options?.persist ?? true;
    if (persist) {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT, {
      persist: true,
    });
  }, [theme, setTheme]);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
